// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the words of the "What Konvo knows" page, and the helpers that turn the page's data
// (KnowsModel keys, dates) into them.

import {getCachedDateTimeFormat} from '@app/features/i18n/utils/IntlCache';
import type {
	KnowsFactKind,
	KnowsHistoryEntry,
	KnowsNote,
	KnowsTopic,
	KnowsTriggerKind,
	LearnedView,
} from '@app/features/konvo_knows/utils/KnowsModel';
import {noteOrigin} from '@app/features/konvo_knows/utils/KnowsModel';
import {getCurrentLocale} from '@app/features/user/utils/LocaleUtils';
import type {I18n, MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';

export const KNOWS_TAB_DESCRIPTOR = msg({
	message: 'What Konvo knows',
	comment: 'User settings tab (under Assistant) listing what the Konvo assistant remembers about the user.',
});

export const TOPIC_DESCRIPTORS: Record<KnowsTopic, MessageDescriptor> = {
	you: msg({message: 'About you', comment: 'What Konvo knows: heading of the notes about the user themself.'}),
	preferences: msg({
		message: 'How you like things',
		comment: 'What Konvo knows: heading of the notes about the user’s preferences (reply style, meetings).',
	}),
	people: msg({
		message: 'People',
		comment: 'What Konvo knows: heading of the notes about people the user works with.',
	}),
	work: msg({message: 'Work', comment: 'What Konvo knows: heading of the notes about the user’s work and projects.'}),
};

export const LEARNED_HEADING_DESCRIPTOR = msg({
	message: 'Learned from what you do',
	comment:
		'What Konvo knows: heading of what the assistant worked out from how the user reacts to its suggestions (not notes).',
});

export const FACT_LABEL_DESCRIPTORS: Record<KnowsFactKind, MessageDescriptor> = {
	place: msg({message: 'Where you are', comment: 'What Konvo knows: accessible label of the place fact chip.'}),
	hours: msg({message: 'Working hours', comment: 'What Konvo knows: accessible label of the working-hours fact chip.'}),
	language: msg({message: 'Languages', comment: 'What Konvo knows: accessible label of the languages fact chip.'}),
	company: msg({message: 'Company', comment: 'What Konvo knows: accessible label of the company fact chip.'}),
	role: msg({message: 'Role', comment: 'What Konvo knows: accessible label of the job role fact chip.'}),
};

export const M = {
	history: msg({
		message: 'History',
		comment: 'What Konvo knows: button that lists notes added, changed and forgotten.',
	}),
	historyTitle: msg({
		message: 'What changed',
		comment: 'What Konvo knows: title of the history dialog (notes added, changed and forgotten).',
	}),
	historyEmpty: msg({
		message: 'Nothing has changed yet.',
		comment: 'What Konvo knows: empty history dialog.',
	}),
	historyAdded: msg({message: 'Added', comment: 'What Konvo knows history: a note was added.'}),
	historyChanged: msg({message: 'Changed', comment: 'What Konvo knows history: a note was changed.'}),
	historyForgot: msg({message: 'Forgot', comment: 'What Konvo knows history: a note was forgotten.'}),
	summaryEmpty: msg({
		message: 'Konvo writes a short summary here once it knows a few things about you.',
		comment: 'What Konvo knows: shown in the profile card before there is a summary.',
	}),
	summaryUpdating: msg({
		message: 'Konvo is updating this…',
		comment: 'What Konvo knows: shown in the profile card while the assistant rewrites the summary.',
	}),
	addPlaceholder: msg({
		message: 'Tell Konvo something to remember…',
		comment: 'What Konvo knows: placeholder of the box where the user types a new note.',
	}),
	addPlaceholderShort: msg({
		message: 'Tell Konvo something…',
		comment: 'What Konvo knows: placeholder of the new note box on phones (short).',
	}),
	addLabel: msg({message: 'New note', comment: 'What Konvo knows: accessible label of the new note box.'}),
	added: msg({message: 'Konvo will remember that.', comment: 'What Konvo knows: toast after adding a note.'}),
	duplicate: msg({message: 'Konvo already knows that.', comment: 'What Konvo knows: the note is already there.'}),
	full: msg({
		message: 'Konvo’s notes are full. Forget something first.',
		comment: 'What Konvo knows: the notes have reached their size limit.',
	}),
	failed: msg({message: 'That did not save. Try again.', comment: 'What Konvo knows: a change failed.'}),
	fromChat: msg({
		message: 'From your chat · {date}',
		comment: 'What Konvo knows: where a note came from, with its date.',
	}),
	youAdded: msg({message: 'You added · {date}', comment: 'What Konvo knows: the user typed this note, with its date.'}),
	savedBefore: msg({
		message: 'Saved before',
		comment: 'What Konvo knows: a note from the old single memory box, before notes had dates.',
	}),
	sourceChat: msg({message: 'From your chat', comment: 'What Konvo knows: tooltip of the chat source icon.'}),
	sourceYou: msg({message: 'You added this', comment: 'What Konvo knows: tooltip of the user source icon.'}),
	change: msg({message: 'Change', comment: 'What Konvo knows: edit a note (icon button and menu item).'}),
	forget: msg({message: 'Forget', comment: 'What Konvo knows: remove a note (icon button and menu item).'}),
	moveTo: msg({message: 'Move to', comment: 'What Konvo knows: menu to put a note under another heading.'}),
	save: msg({message: 'Save', comment: 'What Konvo knows: save the changed note.'}),
	cancel: msg({message: 'Cancel', comment: 'What Konvo knows: stop changing a note.'}),
	forgotten: msg({
		message: 'Forgotten',
		comment: 'What Konvo knows: shown for a few seconds where a note was forgotten, next to Undo.',
	}),
	undo: msg({message: 'Undo', comment: 'What Konvo knows: Undo button (a note just forgotten, a muted topic).'}),
	reset: msg({
		message: 'Reset',
		comment: 'What Konvo knows: make a suggestion type as frequent as before (button on a learned row).',
	}),
	edit: msg({message: 'Edit', comment: 'What Konvo knows: open the VIP settings (button on a learned row).'}),
	footer: msg({
		message: '{count, plural, one {# note} other {# notes}} · {size} of {max} characters',
		comment: 'What Konvo knows: footer with the number of notes and how full they are.',
	}),
	forgetAll: msg({message: 'Forget everything', comment: 'What Konvo knows: remove every note (button).'}),
	forgetAllTitle: msg({message: 'Forget everything?', comment: 'What Konvo knows: confirm dialog title.'}),
	forgetAllBody: msg({
		message:
			'Konvo forgets all {count, plural, one {# note} other {# notes}} straight away. What it learned from what you do stays; undo those one by one.',
		comment: 'What Konvo knows: confirm dialog text for forgetting every note.',
	}),
	forgotAll: msg({message: 'Konvo forgot everything.', comment: 'What Konvo knows: toast after forgetting all notes.'}),
	empty: msg({
		message: 'Konvo does not know anything about you yet. Tell it something above, or just mention it in a chat.',
		comment: 'What Konvo knows: shown when there are no notes.',
	}),
	loadFailed: msg({
		message: 'Could not load what Konvo knows.',
		comment: 'What Konvo knows: the page failed to load.',
	}),
	retry: msg({message: 'Try again', comment: 'What Konvo knows: reload after a failure.'}),
	noteSheetTitle: msg({message: 'Note', comment: 'What Konvo knows: title of the phone sheet for one note.'}),
	topicLabel: msg({message: 'Under', comment: 'What Konvo knows: label of the heading picker in the note sheet.'}),
	settingsLink: msg({
		message: '{count, plural, =0 {No notes yet} one {# note} other {# notes}}',
		comment: 'Assistant settings: the number of notes shown on the "What Konvo knows" link row.',
	}),
	settingsLinkDescription: msg({
		message: 'What the assistant remembers about you and has learned from what you do. Change or forget any of it.',
		comment: 'Assistant settings: description of the "What Konvo knows" link row.',
	}),
	settingsSectionTitle: msg({
		message: 'What it remembers',
		comment: 'Assistant settings: section title above the link to What Konvo knows.',
	}),
};

const TUNING_TITLES: Record<KnowsTriggerKind, MessageDescriptor> = {
	meeting_soon: msg({message: 'Fewer meeting prep notes', comment: 'What Konvo knows: a learned behaviour.'}),
	morning: msg({message: 'A shorter morning brief', comment: 'What Konvo knows: a learned behaviour.'}),
	vip_mail: msg({message: 'Fewer VIP mail alerts', comment: 'What Konvo knows: a learned behaviour.'}),
	task_overdue: msg({message: 'Fewer overdue task nudges', comment: 'What Konvo knows: a learned behaviour.'}),
	commitment_due: msg({message: 'Fewer promise reminders', comment: 'What Konvo knows: a learned behaviour.'}),
	chat_message: msg({message: 'Fewer chat suggestions', comment: 'What Konvo knows: a learned behaviour.'}),
	waiting: msg({
		message: 'Waits longer before bringing up unanswered questions',
		comment: 'What Konvo knows: a learned behaviour about questions waiting on a reply.',
	}),
};

const SKIPPED = msg({
	message: 'You skipped the last {count}',
	comment: 'What Konvo knows: why a suggestion type became rarer (the user ignored the last N).',
});
const UNWANTED = msg({
	message: 'You marked {count} as unwanted',
	comment: 'What Konvo knows: why a suggestion type became rarer (the user marked N as unwanted).',
});
const MUTED_TITLE = msg({
	message: 'Not suggesting: {title}',
	comment: 'What Konvo knows: a topic the user marked unwanted; {title} is the suggestion it was about.',
});
const MUTED_UNTIL = msg({message: 'Until {date}', comment: 'What Konvo knows: when a muted topic comes back.'});
const VIPS_TITLE = msg({
	message: '{names} can interrupt you',
	comment: 'What Konvo knows: the user’s VIPs; {names} is a list like "Gustav, Discovery and iCAM".',
});
const VIPS_SUB = msg({message: 'Your VIPs', comment: 'What Konvo knows: sub line of the VIPs row.'});

/** "3 Oct" (with the year when it is not this year), in the app's locale. */
export function shortDate(iso: string): string {
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return '';
	const sameYear = date.getFullYear() === new Date().getFullYear();
	return getCachedDateTimeFormat(getCurrentLocale(), {
		day: 'numeric',
		month: 'short',
		year: sameYear ? undefined : 'numeric',
	}).format(date);
}

/** The hover line of a note: where it came from and when. */
export function noteOriginText(i18n: I18n, note: KnowsNote): string {
	switch (noteOrigin(note)) {
		case 'chat':
			return i18n._(M.fromChat, {date: shortDate(note.created_at)});
		case 'you':
			return i18n._(M.youAdded, {date: shortDate(note.created_at)});
		case 'before':
			return i18n._(M.savedBefore);
	}
}

function listText(names: ReadonlyArray<string>): string {
	try {
		return new Intl.ListFormat(getCurrentLocale(), {style: 'long', type: 'conjunction'}).format(names);
	} catch {
		return names.join(', ');
	}
}

export function learnedTexts(i18n: I18n, view: LearnedView): {title: string; sub: string; action: string} {
	switch (view.type) {
		case 'tuning':
			return {
				title: i18n._(TUNING_TITLES[view.kind]),
				sub: i18n._(view.detail === 'unwanted' ? UNWANTED : SKIPPED, {count: view.count}),
				action: i18n._(M.reset),
			};
		case 'muted':
			return {
				title: i18n._(MUTED_TITLE, {title: view.title}),
				sub: i18n._(MUTED_UNTIL, {date: shortDate(view.until)}),
				action: i18n._(M.undo),
			};
		case 'vips':
			return {
				title: i18n._(VIPS_TITLE, {names: listText(view.names)}),
				sub: i18n._(VIPS_SUB),
				action: i18n._(M.edit),
			};
	}
}

export function historyActionText(i18n: I18n, action: KnowsHistoryEntry['action']): string {
	switch (action) {
		case 'added':
			return i18n._(M.historyAdded);
		case 'changed':
			return i18n._(M.historyChanged);
		case 'forgot':
			return i18n._(M.historyForgot);
	}
}
